import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Avatar,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Divider,
  Flex,
  useToast,
} from '@chakra-ui/react';
import { useData } from '../../../../data';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { format } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const RSVPStatsModal = ({ isOpen, onClose, event }) => {
  const [rsvpData, setRsvpData] = useState(null);

  const {
    data: rsvpDetails,
    isLoading: isLoadingRsvp,
    refetch: refetchRsvp,
  } = useData(
    event ? `admins/events/${event.id}/rsvp-details` : null,
    30000,
    {},
    1,
    true,
  );

  useEffect(() => {
    if (rsvpDetails) {
      setRsvpData(rsvpDetails.data);
    }
  }, [rsvpDetails]);

  const chartData = {
    labels:
      rsvpData?.trend?.map((item) => format(new Date(item.date), 'MMM dd')) ||
      [],
    datasets: [
      {
        label: 'Going',
        data: rsvpData?.trend?.map((item) => item.going) || [],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Interested',
        data: rsvpData?.trend?.map((item) => item.interested) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },

      {
        label: 'Not Going',
        data: rsvpData?.trend?.map((item) => item.not_attending) || [],
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'RSVP Trends Over Time',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const UserList = ({ users, status }) => (
    <VStack spacing={3} align="stretch" maxH="300px" overflowY="auto">
      {users && users.length > 0 ? (
        users.map((user, index) => (
          <Box
            key={index}
            p={3}
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
          >
            <HStack spacing={3}>
              <Avatar
                size="sm"
                name={`${user.first_name} ${user.last_name}`}
                src={user.profile_image}
              />
              <Box flex="1">
                <Text fontWeight="medium">
                  {user.first_name} {user.last_name}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {user.email}
                </Text>
              </Box>
              <Badge
                colorScheme={status === 'going' ? 'green' : 'blue'}
                variant="subtle"
              >
                {status === 'going' ? 'Going' : 'Interested'}
              </Badge>
            </HStack>
          </Box>
        ))
      ) : (
        <Box p={4} textAlign="center" color="gray.500">
          No users have selected "{status === 'going' ? 'Going' : 'Interested'}"
          status
        </Box>
      )}
    </VStack>
  );

  if (!event) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        w={{ base: '90%', md: '500px' }}
        maxW={{ base: '90%', md: '500px' }}
        maxH="90vh"
        overflow="hidden"
      >
        <ModalHeader>RSVP Statistics - {event.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto" className="hide-scroll">
          {isLoadingRsvp ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="lg" />
            </Flex>
          ) : rsvpData ? (
            <VStack spacing={6} align="stretch">
              {/* Summary Stats */}
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="lg" fontWeight="semibold" mb={3}>
                  Summary
                </Text>
                <HStack spacing={6}>
                  <Box textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {rsvpData.going?.length || 0}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Going
                    </Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {rsvpData.interested?.length || 0}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Interested
                    </Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="gray.600">
                      {(rsvpData.going?.length || 0) +
                        (rsvpData.interested?.length || 0)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Total RSVPs
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {/* RSVP Trends Chart */}
              {rsvpData.trend && rsvpData.trend.length > 0 && (
                <Box
                  p={4}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Text fontSize="lg" fontWeight="semibold" mb={3}>
                    RSVP Trends
                  </Text>
                  <Box h="300px">
                    <Line data={chartData} options={chartOptions} />
                  </Box>
                </Box>
              )}

              {/* User Lists */}
              <Tabs variant="enclosed">
                <TabList>
                  <Tab>Going ({rsvpData.going?.length || 0})</Tab>
                  <Tab>Interested ({rsvpData.interested?.length || 0})</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <UserList users={rsvpData.going} status="going" />
                  </TabPanel>
                  <TabPanel>
                    <UserList users={rsvpData.interested} status="interested" />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          ) : (
            <Box p={4} textAlign="center" color="gray.500">
              No RSVP data available for this event.
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RSVPStatsModal;
